import React, { FC, useEffect, useRef, useState } from "react";
import {
	Box,
	TableCell,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
	TableContainer,
	Table,
	TableBody,
	TablePagination,
	Paper,
	InputBase,
	Stack,
} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ESRState } from "../types/data";
import { UsersManagementRow } from "./UsersManagementRow";
import { listAllUsers, UPDATE_USER_QUERY, RESET_USER_QUERY } from "../actions/form";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { SearchOutlined } from "@mui/icons-material";
import {DataAction} from "../types/action";
import { FacetFilter } from "./FacetFilter";
import { useGroupFacets } from "../hooks/useGroupFacets";

type Order = "asc" | "desc";

export interface UserTableData {
	created: string;
	email: string;
	name: string;
	enabled: boolean;
	groups: string[];
	access_level: string;
	actions: boolean;
}

interface EnhancedTableProps {
	order: Order;
	orderBy: string;
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof UserTableData,
	) => void;
}

interface HeadCell {
	id: keyof UserTableData;
	label: string;
	numeric: boolean;
}

const headCells: readonly HeadCell[] = [
	{
		id: "created",
		numeric: false,
		label: "Registered",
	},
	{
		id: "email",
		numeric: false,
		label: "Email",
	},
	{
		id: "name",
		numeric: false,
		label: "User Name",
	},
	{
		id: "groups",
		numeric: false,
		label: "Groups",
	},
	{
		id: "access_level",
		numeric: false,
		label: "Access Level",
	},

	{
		id: "enabled",
		numeric: false,
		label: "Enabled",
	},
	{
		id: "actions",
		numeric: false,
		label: "Actions",
	},
];

function EnhancedTableHead(props: EnhancedTableProps) {
	const { order, orderBy, onRequestSort } = props;
	const createSortHandler =
		(property: keyof UserTableData) => (event: React.MouseEvent<unknown>) => {
			onRequestSort(event, property);
		};

	return (
		<TableHead>
			<TableRow>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={headCell.numeric ? "right" : "left"}
						sortDirection={orderBy === headCell.id ? order : false}
					>
						{headCell.id === 'created' || headCell.id === 'email' ? (
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : "asc"}
							onClick={createSortHandler(headCell.id)}
						>
							{headCell.label}
						</TableSortLabel>) : (
							headCell.label
						)}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

export const RegisterUserList = (): JSX.Element => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [user, setUser] = useCurrentUser();
	const user_list: UserTableData[] = useSelector((state: ESRState) => {
		return Object.keys(state.user_list).map((email: string) => {
			const user = state.user_list[email];
			return {
				created: user.created,
				email: email,
				name: user.first_name + " " + user.last_name,
				enabled: user.enabled,
				groups: user.groups,
				access_level: user.admin ? "Admin" : "User",
				actions: true,
			};
		});
	});

    const facets = useGroupFacets()

	// table sort
	// const facets = useSelector((s: ESRState) => s.user_facets);
	const query = useSelector((s: ESRState) => s.user_sort_query);
	const order = useSelector((s: ESRState) => {
		// If `order_by` is not specified, the list is ordered by ID. The default order for ID is descending.
		const defaultOrder = s.user_sort_query["order_by"] ? "asc" : "desc";
		return s.user_sort_query["order"] || defaultOrder;
	});
	const orderBy = useSelector((s: ESRState) => s.user_sort_query["order_by"] ?? "created");
	const page = useSelector((s: ESRState) => s.user_sort_query["page"] ?? 0);
	const pageSize = useSelector((s: ESRState) => s.user_sort_query["page_size"] ?? 10);
	const [rowsPerPage, setRowsPerPage] = useState(100);

	// reset query on mount
	useEffect(() => {
		dispatch<DataAction>({ type: RESET_USER_QUERY });
	}, []);

	// fetch users when query changes
	useEffect(() => {
		if (user !== undefined && user !== null) {
			dispatch(listAllUsers());
		}
	}, [user, query]);

	// To search by users first name, lastname, emailid
	const [searchString, setSearchString] = useState("");
	const timerRef = useRef(-1);
	useEffect(() => {
		window.clearTimeout(timerRef.current);
		timerRef.current = window.setTimeout(() => {
			dispatch<DataAction>({
				type: UPDATE_USER_QUERY,
				updates: {
					search: searchString,
					page: 0,
				},
			});
		}, 100);
	}, [searchString]);

	useEffect(() => {
		if (location.state?.searchString) {
		setSearchString(location.state.searchString);
		// signup users are still disabled, so they would be hidden by the default Enabled filter
		dispatch<DataAction>({ type: UPDATE_USER_QUERY, updates: { enabled: [] } });
		}
	}, [location.state]);

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof UserTableData,
	) => {
		const isAsc = orderBy === property && order === "asc";
		dispatch<DataAction>({
			type: UPDATE_USER_QUERY,
			updates: {
				order: isAsc ? "desc" : "asc",
				order_by: property,
				page: 0,
			},
		});
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		dispatch<DataAction>({
			type: UPDATE_USER_QUERY,
			updates: {
				page: newPage,
			},
		});
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		dispatch<DataAction>({
			type: UPDATE_USER_QUERY,
			updates: {
				page_size: parseInt(event.target.value, 10),
				page: 0,
			},
		});
	};

	// Avoid a layout jump when reaching the last page with empty rows.
	const emptyRows =
		page > 0 ? Math.max(0, (1 + page) * rowsPerPage - user_list.length) : 0;

	const visibleRows = React.useMemo(
		() => user_list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
		[user, user_list, order, orderBy, page, rowsPerPage],
	);

	return (
		<>
			<Box
				sx={{
					boxSizing: "border-box",
					p: "60px 50px 80px",
					background: "rgba(0, 62, 98, 0.02)",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography fontSize={24} fontWeight={700}>
						Registration Requests
					</Typography>
				</Box>
				<Box
					sx = {{ display: "flex", marginTop: "2%",justifyContent: "right",}}
				>
					<Paper
							square
							elevation={0}
							sx={{
								p: "2px 4px",
								display: "flex",
								alignItems: "center",
								width: 400,
								bgcolor: "#0000000A",
								marginRight: "1%",
							}}
						>
							<InputBase
								sx={{
									ml: 2,
									flex: 1,
									"& input::placeholder": {
										color: "#4a4a4a",
										opacity: 1,
									},
								}}
								value={searchString}
								onChange={(e) => setSearchString(e.target.value)}
								placeholder="Search by name or email id"
							/>
							<SearchOutlined />
					</Paper>
					</Box>

            <Stack
              direction="row"
              gap="12px"
              flexWrap="wrap"
              alignItems="center"
              sx={{ mt: "40px", mb: "30px" }}
            >
              {facets.map(({ key, label, values }) => (
                <Box key={key} sx={{ flex: "0 1 auto" }}>
                  <FacetFilter
                    label={label}
                    value={query[key] ?? []}
                    options={values}
                    onChange={(value) => {
                      dispatch<DataAction>({
                        type: UPDATE_USER_QUERY,
                        updates: { [key]: value, page: 0 },
                      });
                    }}
                  />
                </Box>
              ))}

              <Box sx={{ flex: "0 1 auto" }}>
                <FacetFilter
                  label="Users"
                  value={query["enabled"] ?? []}
                  options={[
                    { label: "Enabled", value: "true" },
                    { label: "Disabled", value: "false" },
                  ]}
                  onChange={(value) => {
                    dispatch<DataAction>({
                      type: UPDATE_USER_QUERY,
                      updates: { enabled: value, page: 0 },
                    });
                  }}
                />
              </Box>
            </Stack>


			</Box>
			<Box sx={{ mx: "50px", mt: "-58px", mb: "50px" }}>
				<TableContainer>
					<Table sx={{ minWidth: 750 }}>
						<EnhancedTableHead
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
						/>
						<TableBody>
							{visibleRows.map((row: UserTableData) => (
								<UsersManagementRow key={row.email} {...row} />
							))}

							{emptyRows > 0 && (
								<TableRow style={{ height: 53 * emptyRows }}>
									<TableCell colSpan={6} />
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[100, 250, 500]}
					component="div"
					count={Object.keys(user_list).length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Box>
		</>
	);
};
